import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, unref as _unref, normalizeClass as _normalizeClass, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-6d04ee73"),n=n(),_popScopeId(),n)
const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = {
  key: 0,
  class: "status-geolocation"
}
const _hoisted_4 = { class: "warning-geolocation" }
const _hoisted_5 = {
  key: 1,
  class: "status-geolocation"
}
const _hoisted_6 = { class: "error-geolocation" }
const _hoisted_7 = {
  key: 2,
  class: "suggestion-formatted-address"
}
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", { class: "formkit-label" }, "Coordonnées pour l'adresse : ", -1))
const _hoisted_9 = { class: "suggestion-formatted-address-alert" }
const _hoisted_10 = {
  key: 5,
  class: "edit-mode-notice"
}

import { ref, watch, onMounted, computed } from "vue";
import ApiService from "@/_services/api.service";
import { maximumDecimalsGeocoding } from "@/_services/geolocation/validations";
import { useI18n } from "vue-i18n";
import FormGeolocationCompletedByPartner from "@/components/forms/product/FormGeolocationCompletedByPartner.vue";

interface GeolocationReference {
    manualMethod: string | null;
    address: string | null;
    zipCode: string | null;
    city: string | null;
    country: string | null;
    latitude: number | null;
    longitude: number | null;
    formattedAddress: string | null;
    status?: string | null;
}

interface GeolocationEmitData extends GeolocationReference {
    index: number | undefined;
}

interface CountryMapping {
    [key: string]: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'FormGeolocation',
  props: {
    'index': {
        type: Number
    },
    'number': {
        type: Number
    },
    'reference': {
        type: Object as () => GeolocationReference,
        default: () => ({
            manualMethod: null,
            address: null,
            zipCode: null,
            city: null,
            country: null,
            latitude: null,
            longitude: null,
            formattedAddress: null
        })
    },
    'optionsCountry': {
        type: Object
    },
    'isEditMode': {
        type: Boolean,
        default: false
    }
},
  emits: ["update:geolocation"],
  setup(__props, { emit }) {

const props = __props;

const { t } = useI18n();

// Define types for better type safety
const validationMessages = {
    maximumDecimalsGeocoding: `${t('ProductForm.Geolocation.validationMessage.maximumDecimalsGeocoding')}`,
    required: `${t('ProductForm.Geolocation.validationMessage.required')}`,
    min: `${t('ProductForm.Geolocation.validationMessage.min')}`,
    max: `${t('ProductForm.Geolocation.validationMessage.max')}`
};

// Define Props


// Define emits to communicate changes to parent


// Define local state variables with proper initialization from props
const localMethod = ref<string>(props.reference.manualMethod || 'address');
const localAddress = ref<string | null>(props.reference.address || null);
const localZipCode = ref<string | null>(props.reference.zipCode || null);
const localCity = ref<string | null>(props.reference.city || null);
const localCountry = ref<string | null>(null); // Will be set properly after ensureCountryCode is defined
const localLatitude = ref<number | null>(props.reference.latitude || null);
const localLongitude = ref<number | null>(props.reference.longitude || null);
const localFormattedAddress = ref<string | null>(props.reference.formattedAddress || null);

// Define refs for UI state
const initialStatus = ref<string | null>(props.reference.status ?? null);
const activateRequestGeolocation = ref<boolean>(false);
const requestingGeolocation = ref<boolean>(false);
const warningRequestGeolocation = ref<string | null>(null);
const errorRequestGeolocation = ref<string | null>(null);
const addressFound = ref<string | null>(null);

// Function to ensure country is always a code, not a full name
const ensureCountryCode = (country: string | null): string | null => {
    if (!country) return null;

    // Mapping of country names to codes
    const countryMapping: CountryMapping = {
        'France': 'FR',
        'Belgique': 'BE',
        'Suisse': 'CH',
        'Luxembourg': 'LU',
        'Allemagne': 'DE',
        'Italie': 'IT',
        'Espagne': 'ES'
    };

    // If country is a full name, convert it to code
    return countryMapping[country] || country;
};

// Now that ensureCountryCode is defined, we can set localCountry
localCountry.value = ensureCountryCode(props.reference.country);

// Define helper functions
const isEmpty = (val: any): boolean => {
    return (val === undefined || val === null || val === '');
};

const updateStatusMessages = (): void => {
    if ('geocode_not_found_by_address' === initialStatus.value) {
        errorRequestGeolocation.value = `${t('ProductForm.Geolocation.zeroResultsGetGeocodeByAddress')}`;
    } else if ('address_not_found_by_geocode' === initialStatus.value) {
        errorRequestGeolocation.value = `${t('ProductForm.Geolocation.zeroResultsGetAddressByGeocode')}`;
    }
};

const resetMessages = (): void => {
    warningRequestGeolocation.value = null;
    errorRequestGeolocation.value = null;
    addressFound.value = null;
};

const canRequestGeocodeByAddress = (): boolean => {
    return !isEmpty(localAddress.value) &&
        !isEmpty(localZipCode.value) &&
        !isEmpty(localCity.value) &&
        !isEmpty(localCountry.value);
};

const canRequestAddressByGeocode = (): boolean => {
    return !isEmpty(localLatitude.value) && !isEmpty(localLongitude.value);
};

// Handler for country change event
const handleCountryChange = (event: any): void => {
    // Ensure country is a code, not a full name
    localCountry.value = ensureCountryCode(event.target ? event.target.value : event);
    inputAddress();
};

// Function to emit changes to parent
const emitChanges = (): void => {
    emit('update:geolocation', {
        manualMethod: localMethod.value,
        address: localAddress.value,
        zipCode: localZipCode.value,
        city: localCity.value,
        country: localCountry.value,
        latitude: localLatitude.value,
        longitude: localLongitude.value,
        formattedAddress: localFormattedAddress.value,
        index: props.index
    });
};

const inputAddress = (): void => {
    resetMessages();

    if ('address' === localMethod.value) {
        localLatitude.value = null;
        localLongitude.value = null;
        localFormattedAddress.value = null;

        // Only activate request if all required fields are filled
        activateRequestGeolocation.value = canRequestGeocodeByAddress();
    }

    emitChanges();
};

const inputGeocode = (): void => {
    resetMessages();

    if ('geocoding' === localMethod.value) {
        localAddress.value = null;
        localZipCode.value = null;
        localCity.value = null;
        localCountry.value = null;
        localFormattedAddress.value = null;

        // Only activate request if all required fields are filled
        activateRequestGeolocation.value = canRequestAddressByGeocode();
    }

    emitChanges();
};

const beforeRequestGeolocation = (): void => {
    resetMessages();
    requestingGeolocation.value = true;
};

const getAddressByGeocode = async (): Promise<void> => {
    beforeRequestGeolocation();

    // Validate inputs before making the request
    if (!canRequestAddressByGeocode()) {
        errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorMissingCoordinates')}`;
        requestingGeolocation.value = false;
        return;
    }

    // Request
    const payload = {
        'latitude': parseFloat(String(localLatitude.value)),
        'longitude': parseFloat(String(localLongitude.value)),
        'isEditMode': props.isEditMode
    };

    try {
        const response = await ApiService.post("geolocation/reverse-geocoding", payload);
        const responseData = response.data.data;

        if ('OK' === responseData.status) {
            localAddress.value = responseData.address;
            localZipCode.value = responseData.zipCode;
            localCity.value = responseData.city;
            localCountry.value = responseData.countryCode;
            localFormattedAddress.value = responseData.formattedAddress;

            if (null === responseData.address || null === responseData.zipCode || null === responseData.city) {
                warningRequestGeolocation.value = `${t('ProductForm.Geolocation.noCompleteResultGetAddressByGeocode')}`;
            }

            activateRequestGeolocation.value = false;
            emitChanges();
        } else if ('ZERO_RESULTS' === responseData.status) {
            errorRequestGeolocation.value = `${t('ProductForm.Geolocation.zeroResultsGetAddressByGeocode')}`;
            activateRequestGeolocation.value = false;
        } else {
            errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorGetAddressByGeocode')}`;
        }
    } catch (error) {
        console.error('Error in reverse geocoding:', error);
        errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorGetAddressByGeocode')}`;
    }

    requestingGeolocation.value = false;
};

const getGeocodeByAddress = async (): Promise<void> => {
    beforeRequestGeolocation();

    // Validate inputs before making the request
    if (!canRequestGeocodeByAddress()) {
        errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorMissingAddressFields')}`;
        requestingGeolocation.value = false;
        return;
    }

    // Ensure country is a code, not a full name
    const countryCode = ensureCountryCode(localCountry.value);

    // Request
    const payload = {
        'address': localAddress.value,
        'zipCode': localZipCode.value,
        'city': localCity.value,
        'countryCode': countryCode,
        'isEditMode': props.isEditMode
    };

    try {
        const response = await ApiService.post("geolocation/geocoding", payload);
        const geocode = response.data.data;

        if ('OK' === geocode.status) {
            localLatitude.value = geocode.latitude;
            localLongitude.value = geocode.longitude;
            localFormattedAddress.value = geocode.formattedAddress;

            addressFound.value = geocode.formattedAddress;
            activateRequestGeolocation.value = false;
            emitChanges();
        } else if ('ZERO_RESULTS' === geocode.status) {
            errorRequestGeolocation.value = `${t('ProductForm.Geolocation.zeroResultsGetGeocodeByAddress')}`;
            activateRequestGeolocation.value = false;
        } else {
            errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorGetGeocodeByAddress')}`;
        }
    } catch (error) {
        console.error('Error in geocoding:', error);
        errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorGetGeocodeByAddress')}`;
    }

    requestingGeolocation.value = false;
};

// Initialize component
onMounted(() => {
    // Ensure country code is correct in the reference data
    if (props.reference && props.reference.country) {
        localCountry.value = ensureCountryCode(props.reference.country);
    }

    // Set initial status messages
    updateStatusMessages();

    // In edit mode, we should have all the data already
    if (props.isEditMode) {
        activateRequestGeolocation.value = true;
    }
});

// Watch for changes in the reference prop to update local state
watch(() => props.reference, (newReference) => {
    if (newReference) {
        localMethod.value = newReference.manualMethod || 'address';
        localAddress.value = newReference.address || null;
        localZipCode.value = newReference.zipCode || null;
        localCity.value = newReference.city || null;
        localCountry.value = ensureCountryCode(newReference.country);
        localLatitude.value = newReference.latitude || null;
        localLongitude.value = newReference.longitude || null;
        localFormattedAddress.value = newReference.formattedAddress || null;
        initialStatus.value = newReference.status ?? null;

        // Set initial status messages
        updateStatusMessages();
    }
}, { deep: true });

// Define watcher for method changes
watch(localMethod, (value) => {
    resetMessages();

    if ('address' === value) {
        // Keep existing address data in edit mode
        if (!props.isEditMode) {
            localLatitude.value = null;
            localLongitude.value = null;
            localFormattedAddress.value = null;
        }
        activateRequestGeolocation.value = canRequestGeocodeByAddress();
    } else if ('geocoding' === value) {
        // Keep existing geocoding data in edit mode
        if (!props.isEditMode) {
            localAddress.value = null;
            localZipCode.value = null;
            localCity.value = null;
            localCountry.value = null;
            localFormattedAddress.value = null;
        }
        activateRequestGeolocation.value = canRequestAddressByGeocode();
    }

    emitChanges();
});

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return ('completed_by_partner' === localMethod.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(FormGeolocationCompletedByPartner, {
          index: props.index,
          number: props.number,
          reference: props.reference,
          optionsCountry: props.optionsCountry
        }, null, 8, ["index", "number", "reference", "optionsCountry"])
      ]))
    : (_openBlock(), _createElementBlock("div", _hoisted_2, [
        _createVNode(_component_FormKit, {
          name: "manualMethod",
          modelValue: localMethod.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((localMethod).value = $event)),
          type: "radio",
          step: "any",
          label: "Méthode",
          options: {
                address: 'Adresse',
                geocoding: 'Latitude/Longitude'                    
            }
        }, null, 8, ["modelValue"]),
        (warningRequestGeolocation.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("small", _hoisted_4, _toDisplayString(warningRequestGeolocation.value), 1)
            ]))
          : _createCommentVNode("", true),
        (errorRequestGeolocation.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("small", _hoisted_6, _toDisplayString(errorRequestGeolocation.value), 1)
            ]))
          : _createCommentVNode("", true),
        _withDirectives(_createVNode(_component_FormKit, {
          modelValue: localAddress.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((localAddress).value = $event)),
          type: "text",
          step: "any",
          label: props.number + '. Adresse*',
          name: "address",
          placeholder: "Adresse",
          onKeyup: inputAddress
        }, null, 8, ["modelValue", "label"]), [
          [_vShow, 'address' === localMethod.value]
        ]),
        _withDirectives(_createVNode(_component_FormKit, {
          modelValue: localZipCode.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((localZipCode).value = $event)),
          type: "text",
          step: "any",
          label: props.number + '. Code Postal*',
          name: "zipCode",
          placeholder: "Code Postal",
          onKeyup: inputAddress
        }, null, 8, ["modelValue", "label"]), [
          [_vShow, 'address' === localMethod.value]
        ]),
        _withDirectives(_createVNode(_component_FormKit, {
          modelValue: localCity.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((localCity).value = $event)),
          type: "text",
          step: "any",
          label: props.number + '. Ville*',
          name: "city",
          placeholder: "Ville",
          onKeyup: inputAddress
        }, null, 8, ["modelValue", "label"]), [
          [_vShow, 'address' === localMethod.value]
        ]),
        _withDirectives(_createVNode(_component_FormKit, {
          modelValue: localCountry.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((localCountry).value = $event)),
          type: "select",
          label: props.number + '. Pays*',
          name: "country",
          placeholder: "Pays",
          options: props.optionsCountry,
          onChange: handleCountryChange
        }, null, 8, ["modelValue", "label", "options"]), [
          [_vShow, 'address' === localMethod.value]
        ]),
        _withDirectives(_createVNode(_component_FormKit, {
          modelValue: localFormattedAddress.value,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((localFormattedAddress).value = $event)),
          disabled: "",
          type: "textarea",
          step: "any",
          rows: "2",
          label: props.number + '. Adresse correspondant aux coordonnées*',
          name: "formattedAddress",
          placeholder: "Adresse complète",
          validation: "required",
          "validation-messages": validationMessages,
          "validation-visibility": "submit"
        }, null, 8, ["modelValue", "label"]), [
          [_vShow, 'geocoding' === localMethod.value]
        ]),
        ('address' === localMethod.value && addressFound.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _hoisted_8,
              _createElementVNode("div", null, [
                _createElementVNode("small", null, _toDisplayString(addressFound.value), 1)
              ]),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("small", null, _toDisplayString(`${_unref(t)('ProductForm.Geolocation.alertVerificationAddress')}`), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        _createVNode(_component_FormKit, {
          disabled: 'address' === localMethod.value,
          modelValue: localLatitude.value,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((localLatitude).value = $event)),
          type: "number",
          step: "any",
          label: props.number + '. Latitude*',
          name: "latitude",
          placeholder: "Latitude",
          validation: "required|min:-90|max:90|maximumDecimalsGeocoding",
          "validation-rules": { maximumDecimalsGeocoding: _unref(maximumDecimalsGeocoding) },
          "validation-messages": validationMessages,
          "validation-visibility": "submit",
          onKeyup: inputGeocode
        }, null, 8, ["disabled", "modelValue", "label", "validation-rules"]),
        _createVNode(_component_FormKit, {
          disabled: 'address' === localMethod.value,
          modelValue: localLongitude.value,
          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((localLongitude).value = $event)),
          type: "number",
          step: "any",
          label: props.number + '. Longitude*',
          name: "longitude",
          placeholder: "Longititude",
          validation: "required|min:-90|max:90|maximumDecimalsGeocoding",
          "validation-rules": { maximumDecimalsGeocoding: _unref(maximumDecimalsGeocoding) },
          "validation-messages": validationMessages,
          "validation-visibility": "submit",
          onKeyup: inputGeocode
        }, null, 8, ["disabled", "modelValue", "label", "validation-rules"]),
        ('geocoding' !== localMethod.value)
          ? (_openBlock(), _createBlock(_component_FormKit, {
              key: 3,
              disabled: requestingGeolocation.value || (!activateRequestGeolocation.value && !props.isEditMode),
              type: "button",
              label: requestingGeolocation.value ? `${_unref(t)('ProductForm.Geolocation.actions.requestingGeolocation')}` : `${_unref(t)('ProductForm.Geolocation.actions.getGeocode')}`,
              class: _normalizeClass({ 'btn-secondary': true, 'btn-block': true }),
              onClick: getGeocodeByAddress
            }, null, 8, ["disabled", "label"]))
          : _createCommentVNode("", true),
        ('geocoding' === localMethod.value)
          ? (_openBlock(), _createBlock(_component_FormKit, {
              key: 4,
              disabled: requestingGeolocation.value || (!activateRequestGeolocation.value && !props.isEditMode),
              type: "button",
              label: requestingGeolocation.value ? `${_unref(t)('ProductForm.Geolocation.actions.requestingGeolocation')}` : `${_unref(t)('ProductForm.Geolocation.actions.getAddress')}`,
              class: _normalizeClass({ 'btn-secondary': true, 'btn-block': true }),
              onClick: getAddressByGeocode
            }, null, 8, ["disabled", "label"]))
          : _createCommentVNode("", true),
        (props.isEditMode)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createElementVNode("small", null, _toDisplayString(`${_unref(t)('ProductForm.Geolocation.editModeNotice')}`), 1)
            ]))
          : _createCommentVNode("", true)
      ]))
}
}

})